import React, { useState, useRef, useEffect } from "react";
import {
  Activity,
  Save,
  AlertTriangle,
  Plus,
  Pill,
  Pencil,
  Trash2,
  HeartPulse,
  FileText,
  Upload,
  ImageIcon,
  X,
  Stethoscope,
  Syringe,
  ClipboardList,
  Users,
  Calculator,
} from "lucide-react";
import Lifestyle from "./Lifestyle";
import { getProfileStepDetails, updateProfileStep } from "../../api/api";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${
      type === "error" ? "bg-red-500" : type === "success" ? "bg-green-500" : "bg-blue-500"
    } text-white min-w-[300px]`}>
      <span className="text-sm flex-1">{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200 text-xl">×</button>
    </div>
  );
};

const MedicalHistory = () => {
  const [toasts, setToasts] = useState([]);
  const [activeScreen, setActiveScreen] = useState("medicalHistory");
  const [isSaving, setIsSaving] = useState(false);
  
  const [medicalData, setMedicalData] = useState({
    bloodGroup: "",
    height: "",
    weight: "",
    bmi: "",
    bmiStatus: "",
    allergies: false,
    allergiesDetails: "",
    currentMedicalConditions: false,
    currentMedicalConditionsDetails: "",
    pastMedicalHistory: false,
    pastMedicalHistoryDetails: "",
    surgeries: false,
    surgeriesDetails: "",
    currentMedications: "",
    familyMedicalConditions: false,
    familyMedicalConditionsDetails: ""
  });
  
  const [formErrors, setFormErrors] = useState({});
  const fileInputRef = useRef(null);
  
  const [recentUploads, setRecentUploads] = useState([]);

  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    fetchMedicalData();
  }, []);

const fetchMedicalData = async () => {
  try {
    const res = await getProfileStepDetails();
    
    if (res?.status) {
      const medicalFields = res?.profile_completion?.steps?.medical?.fields;
      const personalFields = res?.profile_completion?.steps?.personal?.fields;
      
      const medicalMap = {};
      medicalFields.forEach((item) => {
        medicalMap[item.name] = item.value;
      });
      
      const personalMap = {};
      personalFields.forEach((item) => {
        personalMap[item.name] = item.value;
      });
      
      const bmiValue = personalMap.bmi;
      let bmiStatus = "";
      if (bmiValue) {
        if (bmiValue < 18.5) bmiStatus = "Underweight";
        else if (bmiValue >= 18.5 && bmiValue < 25) bmiStatus = "Normal weight";
        else if (bmiValue >= 25 && bmiValue < 30) bmiStatus = "Overweight";
        else bmiStatus = "Obese";
      }
      
      setMedicalData({
        bloodGroup: personalMap.bloodGroup || "",
        height: personalMap.height || "",
        weight: personalMap.weight || "",
        bmi: personalMap.bmi || "",
        bmiStatus: bmiStatus,
        allergies: medicalMap.allergies || false,
        allergiesDetails: medicalMap.allergiesDetails || "",
        currentMedicalConditions: medicalMap.currentMedicalConditions || false,
        currentMedicalConditionsDetails: medicalMap.currentMedicalConditionsDetails || "",
        pastMedicalHistory: medicalMap.pastMedicalHistory || false,
        pastMedicalHistoryDetails: medicalMap.pastMedicalHistoryDetails || "",
        surgeries: medicalMap.surgeries || false,
        surgeriesDetails: medicalMap.surgeriesDetails || "",
        currentMedications: medicalMap.currentMedications || "",
        familyMedicalConditions: medicalMap.familyMedicalConditions || false,
        familyMedicalConditionsDetails: medicalMap.familyMedicalConditionsDetails || ""
      });
    }
  } catch (error) {
    console.error("Error fetching medical data:", error);
    addToast("Failed to load medical data", "error");
  }
};
  const handleInputChange = (field, value) => {
    setMedicalData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    let hasError = false;
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        addToast(`File ${file.name} is too large. Maximum size is 10MB.`, "error");
        hasError = true;
        return;
      }
      
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        addToast(`File ${file.name} is not supported. Please upload PDF, JPG, or PNG files.`, "error");
        hasError = true;
        return;
      }
      
      const newUpload = {
        id: Date.now() + Math.random(),
        name: file.name.split('.').slice(0, -1).join('.'),
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        type: file.type.includes('pdf') ? 'pdf' : 'image',
        file: file,
      };
      
      setRecentUploads(prev => [newUpload, ...prev]);
    });
    
    if (!hasError && files.length > 0) {
      addToast(`${files.length} file(s) uploaded successfully!`, "success");
    }
    
    event.target.value = '';
  };

  const handleViewFile = (upload) => {
    if (upload.file) {
      const fileURL = URL.createObjectURL(upload.file);
      window.open(fileURL, '_blank');
      setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    } else {
      addToast(`Opening ${upload.name}... (Demo mode)`, "info");
    }
  };

  const handleDeleteFile = (id) => {
    setRecentUploads(prev => prev.filter(upload => upload.id !== id));
    addToast("File deleted successfully", "info");
  };

  const validateForm = () => {
    const errors = {};
    
    if (!medicalData.bloodGroup) errors.bloodGroup = "Blood group is required";
    if (!medicalData.height) {
      errors.height = "Height is required";
    } else if (isNaN(medicalData.height) || medicalData.height < 50 || medicalData.height > 300) {
      errors.height = "Please enter a valid height between 50-300 cm";
    }
    if (!medicalData.weight) {
      errors.weight = "Weight is required";
    } else if (isNaN(medicalData.weight) || medicalData.weight < 10 || medicalData.weight > 500) {
      errors.weight = "Please enter a valid weight between 10-500 kg";
    }
    
    if (medicalData.allergies && !medicalData.allergiesDetails?.trim()) {
      errors.allergiesDetails = "Please specify allergies";
    }
    
    if (medicalData.currentMedicalConditions && !medicalData.currentMedicalConditionsDetails?.trim()) {
      errors.currentMedicalConditionsDetails = "Please specify current medical conditions";
    }
    
    if (medicalData.pastMedicalHistory && !medicalData.pastMedicalHistoryDetails?.trim()) {
      errors.pastMedicalHistoryDetails = "Please specify past medical history";
    }
    
    if (medicalData.surgeries && !medicalData.surgeriesDetails?.trim()) {
      errors.surgeriesDetails = "Please specify surgeries";
    }
    
    if (medicalData.familyMedicalConditions && !medicalData.familyMedicalConditionsDetails?.trim()) {
      errors.familyMedicalConditionsDetails = "Please specify family medical conditions";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSaveChanges = async () => {
  if (validateForm()) {
    setIsSaving(true);
    
    try {
      const medicalPayload = {
        allergies: medicalData.allergies || false,
        allergiesDetails: medicalData.allergiesDetails || null,
        currentMedicalConditions: medicalData.currentMedicalConditions || false,
        currentMedicalConditionsDetails: medicalData.currentMedicalConditionsDetails || null,
        pastMedicalHistory: medicalData.pastMedicalHistory || false,
        pastMedicalHistoryDetails: medicalData.pastMedicalHistoryDetails || null,
        surgeries: medicalData.surgeries || false,
        surgeriesDetails: medicalData.surgeriesDetails || null,
        currentMedications: medicalData.currentMedications || null,
        familyMedicalConditions: medicalData.familyMedicalConditions || false,
        familyMedicalConditionsDetails: medicalData.familyMedicalConditionsDetails || null
      };
      
      const response = await updateProfileStep("medical", medicalPayload);
      
      if (response?.status) {
        addToast("Medical information saved successfully!", "success");
        
        // ✅ IMPORTANT: Refresh all data to get latest personal info (BMI, etc.)
        await fetchMedicalData();
        
        setTimeout(() => {
          setActiveScreen("lifestyle");
        }, 1500);
      } else {
        addToast(response?.message || "Failed to save data", "error");
      }
    } catch (error) {
      console.error("Save error:", error);
      addToast(error.message || "Failed to save medical information", "error");
    } finally {
      setIsSaving(false);
    }
  } else {
    addToast("Please fix the errors before saving", "error");
    const firstErrorField = Object.keys(formErrors)[0];
    if (firstErrorField) {
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  }
};

  if (activeScreen === "lifestyle") {
    return <Lifestyle />;
  }

  return (
    <div className="font-lato">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
            Medical History
          </h2>
          <p className="text-[#98a19b] text-sm">
            Manage your health records, medical conditions, and medications.
          </p>
        </div>

        <button 
          onClick={handleSaveChanges}
          disabled={isSaving}
          className="h-[38px] px-4 bg-[#57b26a] text-white rounded-md text-sm flex items-center gap-2 hover:bg-[#4a9e5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save size={15} />
              Save Changes
            </>
          )}
        </button>
      </div>

      <div className="space-y-6">
 
        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-5">
            <Activity size={18} className="text-green-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Basic Vitals & BMI
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Blood Group <span className="text-red-500">*</span>
              </label>
              <select 
                name="bloodGroup"
                value={medicalData.bloodGroup}
                onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none bg-white focus:ring-1 ${
                  formErrors.bloodGroup
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
              >
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
              {formErrors.bloodGroup && (
                <p className="text-red-500 text-xs mt-1">{formErrors.bloodGroup}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Height (cm) <span className="text-red-500">*</span>
              </label>
              <input
                name="height"
                type="number"
                value={medicalData.height}
                onChange={(e) => handleInputChange("height", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${
                  formErrors.height
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
                placeholder="Enter height in cm"
              />
              {formErrors.height && (
                <p className="text-red-500 text-xs mt-1">{formErrors.height}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Weight (kg) <span className="text-red-500">*</span>
              </label>
              <input
                name="weight"
                type="number"
                value={medicalData.weight}
                onChange={(e) => handleInputChange("weight", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${
                  formErrors.weight
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
                placeholder="Enter weight in kg"
              />
              {formErrors.weight && (
                <p className="text-red-500 text-xs mt-1">{formErrors.weight}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                BMI (Auto-calculated)
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={medicalData.bmi}
                  disabled
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm bg-gray-50"
                />
                <Calculator size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98a19b]" />
              </div>
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                BMI Status
              </label>
              <input
                type="text"
                value={medicalData.bmiStatus}
                disabled
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm bg-gray-50"
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-yellow-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Any Allergies?
            </h3>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={medicalData.allergies}
                  onChange={(e) => handleInputChange("allergies", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                <span className="ml-3 text-sm text-[#24312d]">
                  {medicalData.allergies ? "Yes" : "No"}
                </span>
              </label>
            </div>
          </div>

          {medicalData.allergies && (
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                If yes, specify <span className="text-red-500">*</span>
              </label>
              <textarea
                name="allergiesDetails"
                value={medicalData.allergiesDetails}
                onChange={(e) => handleInputChange("allergiesDetails", e.target.value)}
                rows="3"
                placeholder="e.g., Lactose intolerance, shellfish allergy"
                className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 resize-none ${
                  formErrors.allergiesDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
              />
              {formErrors.allergiesDetails && (
                <p className="text-red-500 text-xs mt-1">{formErrors.allergiesDetails}</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <HeartPulse size={18} className="text-red-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Any Current Medical Conditions?
            </h3>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={medicalData.currentMedicalConditions}
                  onChange={(e) => handleInputChange("currentMedicalConditions", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                <span className="ml-3 text-sm text-[#24312d]">
                  {medicalData.currentMedicalConditions ? "Yes" : "No"}
                </span>
              </label>
            </div>
          </div>

          {medicalData.currentMedicalConditions && (
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                If yes, specify <span className="text-red-500">*</span>
              </label>
              <textarea
                name="currentMedicalConditionsDetails"
                value={medicalData.currentMedicalConditionsDetails}
                onChange={(e) => handleInputChange("currentMedicalConditionsDetails", e.target.value)}
                rows="3"
                placeholder="e.g., Hypertension, Hypothyroidism"
                className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 resize-none ${
                  formErrors.currentMedicalConditionsDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
              />
              {formErrors.currentMedicalConditionsDetails && (
                <p className="text-red-500 text-xs mt-1">{formErrors.currentMedicalConditionsDetails}</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList size={18} className="text-blue-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Any Past Medical History?
            </h3>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={medicalData.pastMedicalHistory}
                  onChange={(e) => handleInputChange("pastMedicalHistory", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                <span className="ml-3 text-sm text-[#24312d]">
                  {medicalData.pastMedicalHistory ? "Yes" : "No"}
                </span>
              </label>
            </div>
          </div>

          {medicalData.pastMedicalHistory && (
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                If yes, specify <span className="text-red-500">*</span>
              </label>
              <textarea
                name="pastMedicalHistoryDetails"
                value={medicalData.pastMedicalHistoryDetails}
                onChange={(e) => handleInputChange("pastMedicalHistoryDetails", e.target.value)}
                rows="3"
                placeholder="e.g., Childhood asthma, Typhoid (2010)"
                className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 resize-none ${
                  formErrors.pastMedicalHistoryDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
              />
              {formErrors.pastMedicalHistoryDetails && (
                <p className="text-red-500 text-xs mt-1">{formErrors.pastMedicalHistoryDetails}</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Syringe size={18} className="text-purple-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Any Surgeries?
            </h3>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={medicalData.surgeries}
                  onChange={(e) => handleInputChange("surgeries", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                <span className="ml-3 text-sm text-[#24312d]">
                  {medicalData.surgeries ? "Yes" : "No"}
                </span>
              </label>
            </div>
          </div>

          {medicalData.surgeries && (
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                If yes, specify <span className="text-red-500">*</span>
              </label>
              <textarea
                name="surgeriesDetails"
                value={medicalData.surgeriesDetails}
                onChange={(e) => handleInputChange("surgeriesDetails", e.target.value)}
                rows="3"
                placeholder="e.g., Gallbladder removal (2018)"
                className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 resize-none ${
                  formErrors.surgeriesDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
              />
              {formErrors.surgeriesDetails && (
                <p className="text-red-500 text-xs mt-1">{formErrors.surgeriesDetails}</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Pill size={18} className="text-green-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Any Medications you are currently consuming?
            </h3>
          </div>

          <div>
            <label className="block text-[13px] text-[#24312d] mb-2">
              If yes, specify
            </label>
            <textarea
              name="currentMedications"
              value={medicalData.currentMedications || ""}
              onChange={(e) => handleInputChange("currentMedications", e.target.value)}
              rows="3"
              placeholder="e.g., Thyroxine 50 mcg daily, Vitamin D weekly"
              className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a] resize-none"
            />
          </div>
        </div>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-indigo-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Any Family Medical Conditions?
            </h3>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={medicalData.familyMedicalConditions}
                  onChange={(e) => handleInputChange("familyMedicalConditions", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                <span className="ml-3 text-sm text-[#24312d]">
                  {medicalData.familyMedicalConditions ? "Yes" : "No"}
                </span>
              </label>
            </div>
          </div>

          {medicalData.familyMedicalConditions && (
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                If yes, specify <span className="text-red-500">*</span>
              </label>
              <textarea
                name="familyMedicalConditionsDetails"
                value={medicalData.familyMedicalConditionsDetails}
                onChange={(e) => handleInputChange("familyMedicalConditionsDetails", e.target.value)}
                rows="3"
                placeholder="e.g., Diabetes (father), Hypertension (mother)"
                className={`w-full border rounded-md px-4 py-2 text-sm outline-none focus:ring-1 resize-none ${
                  formErrors.familyMedicalConditionsDetails
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                }`}
              />
              {formErrors.familyMedicalConditionsDetails && (
                <p className="text-red-500 text-xs mt-1">{formErrors.familyMedicalConditionsDetails}</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-5">
            <FileText size={18} className="text-green-500" />
            <h3 className="text-[16px] font-semibold text-[#24312d]">
              Medical Documents & Reports
            </h3>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div 
              onClick={handleUploadClick}
              className="border-2 border-dashed border-[#dfe5df] bg-[#f7f9f7] rounded-xl min-h-[190px] flex flex-col items-center justify-center text-center px-6 cursor-pointer hover:bg-[#eefcf2] hover:border-green-500 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                <Upload size={18} className="text-green-500" />
              </div>
              <h4 className="text-[15px] font-semibold text-[#24312d] mb-2">
                Click to upload or drag & drop
              </h4>
              <p className="text-[12px] text-[#98a19b]">
                Supported formats: PDF, JPG, PNG (max. 10MB)
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />

            <div className="xl:col-span-2">
              <h4 className="text-[14px] font-semibold text-[#24312d] mb-4">
                Recent Uploads {recentUploads.length > 0 && `(${recentUploads.length})`}
              </h4>

              <div className="space-y-4">
                {recentUploads.map((upload) => (
                  <div
                    key={upload.id}
                    className="border border-[#edf1ed] rounded-lg p-4 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                        upload.type === "pdf" ? "bg-[#fff5f5]" : "bg-[#eefcf2]"
                      }`}>
                        {upload.type === "pdf" ? (
                          <FileText size={18} className="text-red-500" />
                        ) : (
                          <ImageIcon size={18} className="text-green-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className="text-[14px] font-semibold text-[#24312d]">
                          {upload.name}
                        </h5>
                        <p className="text-[12px] text-[#98a19b]">
                          Uploaded on {upload.date} • {upload.size}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewFile(upload)}
                        className="h-[36px] px-4 border border-[#dfe5df] rounded-md text-[13px] text-green-500 hover:bg-green-50 transition"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteFile(upload.id)}
                        className="h-[36px] px-4 border border-[#dfe5df] rounded-md text-[13px] text-red-500 hover:bg-red-50 transition"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                
                {recentUploads.length === 0 && (
                  <div className="text-center py-8 text-[#98a19b]">
                    No documents uploaded yet. Click the upload area to add files.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalHistory;